/**
 * Reports Page
 * Yearly report with stacked bar chart and monthly breakdown
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useYearlyReport, useExportReport } from '../hooks/useYearlyReport';
import { useRevenueEntries } from '../hooks/useRevenueEntries';
import { useExpenseEntries } from '../hooks/useExpenseEntries';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from '../components/icons/CategoryIcons';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { getCategoryName } from '../components/ui/CategoryLabel';
import { type SupportedCurrency, CURRENCY_SYMBOLS, DEFAULT_EXCHANGE_RATES } from '../types';

// Format number for display (uses pt-BR for BRL, en-US for others)
const formatCurrencyValue = (value: number, currency: SupportedCurrency): string => {
  return value.toLocaleString(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Convert BRL amount to target currency using provided rates (fallback to defaults)
const convertBRLToTargetCurrency = (
  brlAmount: number,
  targetCurrency: SupportedCurrency,
  rates?: Record<SupportedCurrency, number> | null
): number => {
  if (targetCurrency === 'BRL') return brlAmount;
  const rate = rates?.[targetCurrency] ?? DEFAULT_EXCHANGE_RATES[targetCurrency];
  return brlAmount * rate;
};

// Calculate percentage share of category spend relative to total month spend
const calculatePercentageShare = (categorySpend: number, totalMonthSpend: number): number => {
  if (totalMonthSpend === 0) return 0;
  return Math.round((categorySpend / totalMonthSpend) * 100);
};

// Month translation keys
const MONTH_KEYS = [
  'jan', 'feb', 'mar', 'apr', 'may_short', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
];

export function ReportsPage() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [isExporting, setIsExporting] = useState(false);
  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  // Get translated month names
  const months = MONTH_KEYS.map(key => t(`months.${key}`));

  const { report, isLoading, isError } = useYearlyReport(selectedYear);
  const { downloadCsv } = useExportReport();
  const { isAuthenticated } = useAuthStore();
  const { preferences } = useUserPreferences();
  const currentCurrency = (preferences?.defaultCurrency as SupportedCurrency) || 'BRL';

  // Fetch exchange rates from database (for converting aggregated totals to user's currency)
  const { rates: exchangeRates, isLoading: ratesLoading } = useExchangeRates();

  // Get monthly revenue - amounts in BRL converted using currencySnapshots
  const { total: selectedMonthRevenue } = useRevenueEntries(selectedYear, selectedMonth + 1, currentCurrency);

  // Get monthly expenses - amounts in BRL converted using currencySnapshots
  const { total: selectedMonthExpenses } = useExpenseEntries(selectedYear, selectedMonth + 1, currentCurrency);

  // Get unique categories from all months (not just the first month)
  // This ensures users with no January data still see the chart
  const categoriesFromReport = (() => {
    if (!report?.months) return [];
    const categoryMap = new Map<string, typeof report.months[0]['categories'][0]>();
    for (const month of report.months) {
      for (const cat of month.categories || []) {
        if (!categoryMap.has(cat.categoryId)) {
          categoryMap.set(cat.categoryId, cat);
        }
      }
    }
    return Array.from(categoryMap.values());
  })();

  // Prepare chart data from API response with graceful empty handling
  const chartData = report?.months?.map((month) => {
    const data: Record<string, string | number> = { month: months[month.month - 1] || '' };
    (month.categories || []).forEach((cat) => {
      data[cat.categoryId] = cat.total || 0;
    });
    return data;
  }) || [];

  // Check if we have any data to display
  const hasData = chartData.length > 0 && categoriesFromReport.length > 0;

  // Get selected month data
  const selectedMonthData = report?.months.find((m) => m.month === selectedMonth + 1);

  // Custom tooltip for the chart with light/dark mode support
  // Note: Chart data comes from backend in BRL, converted to user's currency for display
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className={`rounded-lg p-3 shadow-xl ${
        isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'
      }`}>
        <p className={`font-medium mb-2 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{entry.name}:</span>
            <span className={`font-mono ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(convertBRLToTargetCurrency(entry.value || 0, currentCurrency, exchangeRates), currentCurrency)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadCsv(selectedYear);
    } catch {
      console.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrevYear = () => setSelectedYear((y) => y - 1);
  const handleNextYear = () => setSelectedYear((y) => Math.min(y + 1, currentYear));

  if (!isAuthenticated) {
    return (
      <div className="px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>{t('errors.unauthorized')}</p>
      </div>
    );
  }

  if (isLoading || ratesLoading) {
    return (
      <div className="px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <div className={`animate-pulse ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div className="px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <p className="text-red-400">{t('errors.fetchFailed')}</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Header with Year Selector */}
      <div className="text-center space-y-2">
        <h2 className={`text-xl font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
          {t('pages.reports.yearlyReport')}
        </h2>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={handlePrevYear}
            className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
          >
            <ChevronLeftIcon className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
          <span className="text-2xl font-bold text-primary-500 min-w-[80px]">
            {selectedYear}
          </span>
          <button
            onClick={handleNextYear}
            disabled={selectedYear >= currentYear}
            className={`p-1 rounded-full transition-colors disabled:opacity-30 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
          >
            <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
        </div>
      </div>

      {/* Yearly Summary - converted to user's currency */}
      <div className="grid grid-cols-3 gap-3">
        <div className={`rounded-lg p-3 text-center ${isDark ? 'bg-slate-900/50' : 'bg-slate-100'}`} data-testid="yearly-income">
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('pages.reports.totalIncome')}</p>
          <p className="text-sm font-semibold text-green-500">
            {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(convertBRLToTargetCurrency(report.yearlyTotals?.totalIncome || 0, currentCurrency, exchangeRates), currentCurrency)}
          </p>
        </div>
        <div className={`rounded-lg p-3 text-center ${isDark ? 'bg-slate-900/50' : 'bg-slate-100'}`} data-testid="yearly-expenses">
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('pages.reports.totalExpenses')}</p>
          <p className="text-sm font-semibold text-red-500">
            {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(convertBRLToTargetCurrency(report.yearlyTotals?.totalExpenses || 0, currentCurrency, exchangeRates), currentCurrency)}
          </p>
        </div>
        <div className={`rounded-lg p-3 text-center ${isDark ? 'bg-slate-900/50' : 'bg-slate-100'}`} data-testid="yearly-balance">
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t('pages.reports.netBalance')}</p>
          <p className={`text-sm font-semibold ${(report.yearlyTotals?.netBalance || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(convertBRLToTargetCurrency(report.yearlyTotals?.netBalance || 0, currentCurrency, exchangeRates), currentCurrency)}
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3">
        {categoriesFromReport.map((cat) => (
          <div key={cat.categoryId} className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: cat.categoryColor }}
            />
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {getCategoryName(t, cat.categoryNameKey, cat.categoryName)?.split(' ')[0] || t('modals.editExpense.category')}
            </span>
          </div>
        ))}
      </div>

      {/* Stacked Bar Chart */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-slate-900/50' : 'bg-slate-100'}`} data-testid="bar-chart-container">
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <XAxis
                dataKey="month"
                tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }}
                axisLine={{ stroke: isDark ? '#334155' : '#cbd5e1' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${Math.round(value)}`}
              />
              <Tooltip content={<CustomTooltip />} />
              {categoriesFromReport.map((cat) => (
                <Bar
                  key={cat.categoryId}
                  dataKey={cat.categoryId}
                  name={getCategoryName(t, cat.categoryNameKey, cat.categoryName)}
                  stackId="a"
                  fill={cat.categoryColor}
                  radius={[0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[200px]">
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              {t('pages.reports.noDataForPeriod')}
            </p>
          </div>
        )}
      </div>

      {/* Month Selector */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
          {months[selectedMonth]}
        </h3>
        <div className="flex gap-1">
          {months.map((_, index) => (
            <button
              key={index}
              onClick={() => setSelectedMonth(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                selectedMonth === index
                  ? 'bg-primary-500'
                  : isDark
                    ? 'bg-slate-700 hover:bg-slate-600'
                    : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Monthly Revenue Card */}
      {selectedMonthRevenue > 0 && (
        <div className={`rounded-lg p-4 ${isDark ? 'bg-emerald-900/30 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {t('pages.profile.monthlyIncome')}
              </p>
              <p className={`text-xl font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`} data-testid="monthly-revenue-display">
                {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(selectedMonthRevenue, currentCurrency)}
              </p>
            </div>
            <div className={`text-3xl ${isDark ? 'text-emerald-500/30' : 'text-emerald-300'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
          </div>
          {selectedMonthData && (
            <div className={`mt-2 pt-2 border-t ${isDark ? 'border-emerald-800/50' : 'border-emerald-200'}`}>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {t('pages.home.balance')}: <span className={`font-medium ${(selectedMonthRevenue - selectedMonthExpenses) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(selectedMonthRevenue - selectedMonthExpenses, currentCurrency)}
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Monthly Breakdown Table - category data converted to user's currency */}
      <div className="space-y-2">
        {(selectedMonthData?.categories || []).map((cat) => {
          const percentageShare = calculatePercentageShare(
            cat.total || 0,
            selectedMonthData?.totalExpenses || 0
          );

          return (
            <div
              key={cat.categoryId}
              className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                isDark ? 'bg-slate-900/30' : 'bg-slate-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: cat.categoryColor }}
                />
                <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  {getCategoryName(t, cat.categoryNameKey, cat.categoryName)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-sm ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                  {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(convertBRLToTargetCurrency(cat.total || 0, currentCurrency, exchangeRates), currentCurrency)}
                </span>
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-200 text-slate-600'
                }`}>
                  {percentageShare}%
                </span>
              </div>
            </div>
          );
        })}
        {(!selectedMonthData?.categories || selectedMonthData.categories.length === 0) && (
          <p className={`text-center py-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {t('pages.reports.noDataForPeriod')}
          </p>
        )}
      </div>

      {/* Monthly Total - using stored exchange rates from individual entries */}
      {selectedMonthData && (
        <div className={`flex items-center justify-between py-2 px-3 border-t ${
          isDark ? 'border-slate-800' : 'border-slate-200'
        }`} data-testid="monthly-total">
          <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
            {t('pages.budget.monthTotal')}
          </span>
          <span className="text-primary-500 font-bold font-mono">
            {CURRENCY_SYMBOLS[currentCurrency]} {formatCurrencyValue(selectedMonthExpenses, currentCurrency)}
          </span>
        </div>
      )}

      {/* Export Button */}
      <button
        onClick={handleExport}
        disabled={isExporting}
        className={`w-full flex items-center justify-center gap-2 py-3 border rounded-xl transition-colors disabled:opacity-50 ${
          isDark
            ? 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
            : 'border-slate-300 text-slate-600 hover:bg-slate-100 hover:border-slate-400'
        }`}
      >
        <DownloadIcon className="w-5 h-5" />
        <span className="font-medium">
          {isExporting ? t('common.loading') : t('pages.reports.export')}
        </span>
      </button>
    </div>
  );
}
